/**
 * Service centralisé pour la gestion des sons de notification
 * Utilise le son réel de téléphone classique de Pixabay
 * Volume réglable, répétition jusqu'à action sur commande
 */

class AudioService {
  constructor() {
    this.lastPlayTime = 0;
    this.cooldown = 8000; // 8 secondes par défaut (anti-spam pour son unique)
    this.enabled = this.loadSetting('enabled', true);
    this.volume = this.loadSetting('volume', 0.7); // Volume par défaut plus fort
    
    // Cache des fichiers audio
    this.audioCache = {};
    this.audioElement = null;
    
    // Gestion des sons en cours (pour pouvoir les arrêter)
    this.activeAudioInstances = new Set();
    this.isRepeating = false;
    this.repeatInterval = null;
  }

  loadSetting(key, defaultValue) {
    try {
      const value = localStorage.getItem(`notifications_${key}`);
      if (value === null) return defaultValue;
      
      if (key === 'enabled') {
        return value !== 'false';
      } else if (key === 'volume') {
        const vol = parseFloat(value);
        return isNaN(vol) ? defaultValue : Math.max(0, Math.min(1, vol));
      } else if (key === 'repeatInterval') {
        const interval = parseFloat(value);
        return isNaN(interval) ? defaultValue : Math.max(1, Math.min(10, interval));
      }
      return defaultValue;
    } catch (e) {
      return defaultValue;
    }
  }

  saveSetting(key, value) {
    try {
      localStorage.setItem(`notifications_${key}`, String(value));
    } catch (e) {
      console.warn('[AudioService] Erreur sauvegarde setting:', e);
    }
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    this.saveSetting('enabled', enabled);
    if (!enabled) {
      this.stopAll();
    }
  }

  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    this.saveSetting('volume', this.volume);
    // Mettre à jour le volume de toutes les instances actives
    this.activeAudioInstances.forEach(audio => {
      audio.volume = this.volume;
    });
  }


  setCooldown(ms) {
    this.cooldown = Math.max(0, ms);
  }

  /**
   * Charger le fichier audio (une seule fois)
   */
  async loadAudio() {
    if (this.audioElement) {
      return this.audioElement;
    }

    try {
      // Essayer différents formats et chemins (Vite sert les fichiers depuis /public/)
      const audioPaths = [
        '/sounds/old-phone-ring.mp3',
        '/sounds/old-phone-ring.wav',
        '/sounds/old-phone-ring.ogg',
        '/sounds/old-phone.mp3',
        '/sounds/old-phone.wav',
        '/sounds/old-phone.ogg'
      ];

      let audioElement = null;
      let loadedPath = null;

      for (const path of audioPaths) {
        try {
          audioElement = new Audio(path);
          
          // Vérifier que le fichier existe en testant le chargement
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('Timeout loading audio'));
            }, 3000); // Timeout augmenté à 3 secondes
            
            audioElement.addEventListener('canplaythrough', () => {
              clearTimeout(timeout);
              resolve();
            }, { once: true });
            
            audioElement.addEventListener('canplay', () => {
              clearTimeout(timeout);
              resolve();
            }, { once: true });
            
            audioElement.addEventListener('error', (e) => {
              clearTimeout(timeout);
              reject(new Error(`Erreur chargement ${path}: ${e.message || 'Fichier non trouvé'}`));
            }, { once: true });
            
            audioElement.load();
          });
          
          loadedPath = path;
          console.log(`[AudioService] ✅ Fichier audio trouvé: ${path}`);
          break;
        } catch (err) {
          console.debug(`[AudioService] Impossible de charger ${path}:`, err.message);
          continue;
        }
      }

      if (!audioElement || !loadedPath) {
        console.warn('[AudioService] ⚠️ Aucun fichier audio trouvé dans /public/sounds/');
        console.warn('[AudioService] Fichiers recherchés:', audioPaths);
        console.warn('[AudioService] Un son synthétique sera utilisé en fallback');
        throw new Error('Aucun fichier audio trouvé. Placez old-phone-ring.mp3 dans /public/sounds/');
      }

      // Précharger
      audioElement.preload = 'auto';
      audioElement.volume = this.volume;
      
      this.audioElement = audioElement;
      console.log(`[AudioService] Fichier audio chargé: ${loadedPath}`);
      
      return audioElement;
    } catch (error) {
      console.error('[AudioService] Erreur chargement audio:', error);
      throw error;
    }
  }

  /**
   * Arrêter tous les sons en cours (sans arrêter la répétition)
   */
  stopAll() {
    // Arrêter toutes les instances audio
    this.activeAudioInstances.forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) {
        console.debug('[AudioService] Erreur arrêt audio:', e);
      }
    });
    this.activeAudioInstances.clear();
  }

  /**
   * Arrêter la répétition
   */
  stopRepeat() {
    // Arrêter l'intervalle de répétition
    if (this.repeatInterval) {
      clearInterval(this.repeatInterval);
      this.repeatInterval = null;
    }
    // Marquer comme non répétitif
    this.isRepeating = false;
    // Arrêter tous les sons en cours (mais pas récursif)
    this.stopAll();
    console.log('[AudioService] 🔇 Répétition arrêtée');
  }

  /**
   * Fallback : créer un son synthétique de téléphone classique
   * Utilisé si le fichier audio n'est pas disponible
   */
  playFallbackSound(audioContext, startTime) {
    // Son de téléphone classique synthétique : deux fréquences pour un effet réaliste
    const freq1 = 440; // La
    const freq2 = 554.37; // Do#
    
    const ringDuration = 0.3; // Durée d'une sonnerie
    const pauseDuration = 0.1; // Pause entre sonneries
    const totalDuration = 1.0; // Durée totale du son
    
    const ringCount = Math.ceil(totalDuration / (ringDuration + pauseDuration));
    
    for (let i = 0; i < ringCount; i++) {
      const ringStart = startTime + (i * (ringDuration + pauseDuration));
      
      // Première fréquence
      const osc1 = audioContext.createOscillator();
      const gain1 = audioContext.createGain();
      osc1.connect(gain1);
      gain1.connect(audioContext.destination);
      
      osc1.frequency.value = freq1;
      osc1.type = 'sine';
      
      gain1.gain.setValueAtTime(0, ringStart);
      gain1.gain.linearRampToValueAtTime(this.volume * 1.2, ringStart + 0.01);
      gain1.gain.setValueAtTime(this.volume * 1.2, ringStart + ringDuration * 0.7);
      gain1.gain.exponentialRampToValueAtTime(0.01, ringStart + ringDuration);
      
      osc1.start(ringStart);
      osc1.stop(ringStart + ringDuration);
      
      // Deuxième fréquence (harmonique)
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      
      osc2.frequency.value = freq2;
      osc2.type = 'sine';
      
      gain2.gain.setValueAtTime(0, ringStart);
      gain2.gain.linearRampToValueAtTime(this.volume * 0.8, ringStart + 0.01);
      gain2.gain.setValueAtTime(this.volume * 0.8, ringStart + ringDuration * 0.7);
      gain2.gain.exponentialRampToValueAtTime(0.01, ringStart + ringDuration);
      
      osc2.start(ringStart);
      osc2.stop(ringStart + ringDuration);
      
      if (i === ringCount - 1) {
        osc2.onended = () => {
          if (audioContext.state !== 'closed') {
            audioContext.close();
          }
        };
      }
    }
  }

  /**
   * Jouer le son UNE SEULE FOIS pour test (jamais de répétition)
   * @param {number} volume - Volume à utiliser (optionnel)
   * @returns {Promise<void>}
   */
  async playTest(volume = null) {
    // Arrêter toute répétition en cours
    this.stopRepeat();
    
    const originalVolume = this.volume;
    if (volume !== null) {
      this.setVolume(volume);
    }
    
    try {
      // Essayer de charger le fichier audio
      let audio = null;
      try {
        audio = await this.loadAudio();
      } catch (loadError) {
        console.warn('[AudioService Test] Fichier audio non trouvé, utilisation du son synthétique:', loadError.message);
        audio = null;
      }
      
      if (audio) {
        // Utiliser le fichier audio réel - jouer en entier UNE SEULE FOIS
        const audioClone = audio.cloneNode();
        audioClone.volume = this.volume;
        audioClone.currentTime = 0;
        audioClone.loop = false; // IMPORTANT : pas de boucle
        
        // Jouer le son
        await audioClone.play();
        console.log(`[AudioService Test] ✅ Son réel joué, volume: ${this.volume}`);
        
        // Attendre la fin du son puis nettoyer
        audioClone.addEventListener('ended', () => {
          audioClone.pause();
          audioClone.currentTime = 0;
        }, { once: true });
        
      } else {
        // Fallback : son synthétique avec durée limitée
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        
        // Créer un son simple de 0.5 seconde
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 440;
        oscillator.type = 'sine';
        gainNode.gain.value = this.volume;
        
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.5); // 0.5 seconde seulement
        
        console.log(`[AudioService Test] ✅ Son synthétique joué (0.5s), volume: ${this.volume}`);
      }
    } catch (error) {
      console.error('[AudioService Test] Erreur:', error);
      throw error;
    } finally {
      // Restaurer le volume original
      if (volume !== null) {
        this.setVolume(originalVolume);
      }
    }
  }

  /**
   * Jouer le son de notification (une seule fois)
   * @param {boolean} force - Ignorer enabled et cooldown (pour tests)
   * @returns {Promise<void>}
   */
  async play(force = false) {
    // Si force = true, ignorer enabled et cooldown (pour les tests)
    if (!force && !this.enabled) {
      console.debug('[AudioService] Son désactivé');
      return;
    }

    // Vérifier le cooldown (sauf si force = true)
    if (!force) {
      const currentTime = Date.now();
      if (currentTime - this.lastPlayTime < this.cooldown) {
        console.debug('[AudioService] Cooldown actif, son ignoré');
        return;
      }
    }
    
    try {
      // Essayer de charger le fichier audio
      let audio = null;
      try {
        audio = await this.loadAudio();
      } catch (loadError) {
        console.warn('[AudioService] Fichier audio non trouvé, utilisation du son synthétique:', loadError.message);
        audio = null;
      }
      
      if (audio) {
        // Utiliser le fichier audio réel - jouer pendant 10 secondes maximum
        const audioClone = audio.cloneNode();
        audioClone.volume = this.volume;
        audioClone.currentTime = 0; // Commencer au début
        audioClone.loop = false; // Pas de boucle
        
        // Ajouter à la liste des instances actives
        this.activeAudioInstances.add(audioClone);
        
        // Arrêter après 10 secondes maximum
        const stopTimeout = setTimeout(() => {
          audioClone.pause();
          audioClone.currentTime = 0;
          this.activeAudioInstances.delete(audioClone);
        }, 10000); // 10 secondes
        
        // Nettoyer quand le son se termine naturellement
        audioClone.addEventListener('ended', () => {
          clearTimeout(stopTimeout);
          this.activeAudioInstances.delete(audioClone);
        }, { once: true });
        
        // Jouer le son
        try {
          await audioClone.play();
          console.log(`[AudioService] ✅ Son réel joué (10s max), volume: ${this.volume}`);
          
          this.lastPlayTime = Date.now();
        } catch (playError) {
          clearTimeout(stopTimeout);
          this.activeAudioInstances.delete(audioClone);
          console.error('[AudioService] Erreur lecture audio:', playError);
          // Si la lecture échoue (restriction navigateur), utiliser le fallback
          throw playError;
        }
      } else {
        // Fallback : son synthétique
        console.log('[AudioService] Utilisation du son synthétique (fichier audio non disponible)');
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        console.log('[AudioService] AudioContext créé, état:', audioContext.state);
        
        // Si l'AudioContext est suspendu, le reprendre
        if (audioContext.state === 'suspended') {
          console.log('[AudioService] AudioContext suspendu, reprise...');
          await audioContext.resume();
          console.log('[AudioService] AudioContext repris, état:', audioContext.state);
        }
        
        console.log('[AudioService] Volume:', this.volume);
        this.playFallbackSound(audioContext, audioContext.currentTime);
        
        this.lastPlayTime = Date.now();
        console.log(`[AudioService] ✅ Son joué (synthétique)`);
        console.warn('[AudioService] ⚠️ Fichier audio manquant. Placez old-phone-ring.mp3 dans /public/sounds/ pour utiliser le son réel de Pixabay');
      }
    } catch (error) {
      console.error('[AudioService] Erreur lecture audio:', error);
      // Ne pas bloquer l'application
    }
  }

  /**
   * Démarrer la répétition du son jusqu'à ce que stopRepeat() soit appelé
   * @param {number} intervalMs - Intervalle entre chaque répétition (en ms)
   */
  async startRepeat(intervalMs = 3000) {
    // S'assurer qu'aucune répétition n'est déjà en cours
    if (this.isRepeating) {
      console.log('[AudioService] ⚠️ Répétition déjà en cours, arrêt de l\'ancienne avant de démarrer une nouvelle');
      this.stopRepeat();
      // Attendre un peu pour s'assurer que l'ancienne est bien arrêtée
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (!this.enabled) {
      console.log('[AudioService] Son désactivé, impossible de démarrer la répétition');
      return;
    }

    this.isRepeating = true;
    
    // Jouer immédiatement
    await this.play(true); // Force = true pour ignorer cooldown
    
    // Puis répéter à l'intervalle spécifié
    this.repeatInterval = setInterval(async () => {
      if (!this.isRepeating || !this.enabled) {
        console.log('[AudioService] Condition d\'arrêt détectée, arrêt de la répétition');
        this.stopRepeat();
        return;
      }
      await this.play(true); // Force = true pour ignorer cooldown
    }, intervalMs);
    
    console.log(`[AudioService] 🔔 Répétition démarrée (intervalle: ${intervalMs}ms)`);
  }

  /**
   * Réinitialiser le cooldown (utile pour tests)
   */
  resetCooldown() {
    this.lastPlayTime = 0;
  }
}

// Instance singleton
const audioService = new AudioService();

// Exposer globalement pour les composants qui ne peuvent pas importer directement
if (typeof window !== 'undefined') {
  window.audioService = audioService;
}

export default audioService;
